import { has, findIndex, find } from 'lodash';
import { EDITOR } from 'constants/actionTypes';
import request from 'utils/request_ajax';
import { uploadAndRecordDocument } from 'misc/documentUpload';
import { getElement, hasChildren } from 'selectors/editor';
import { getCommentsCount } from 'selectors/commentary';
import {
  autosaveSuccess,
  autosaveInProgress,
  handleError,
  autosaveFail,
} from 'actions/editor';
import { routes } from 'constants/config';
import * as FileStack from 'misc/imageUpload';
import { fetchCommentary, hideCommentary } from './commentary';

/*eslint no-undef: "error"*/
/*eslint camelcase:0*/

/* action creators */

const deleteElement = id => ({
  type: EDITOR.ELEMENT.DELETE,
  id,
});

const deleteChildren = parentId => ({
  type: EDITOR.ELEMENT.DELETE_CHILDREN,
  id: parentId,
});

const setElementValidity = (id, valid) => ({
  type: EDITOR.ELEMENT.SET_VALIDITY,
  id,
  valid,
});

const orderElement = (position, newPosition) => ({
  type: EDITOR.ELEMENT.REORDER,
  position,
  newPosition,
});

const setSectionsWithInvalidElements = sectionsWithInvalidElements => ({
  type: EDITOR.LESSON.REFRESH_INVALID_SECTIONS,
  sectionsWithInvalidElements,
});

const applyChanges = (id, changes, mergeChanges = true) => ({
  type: EDITOR.ELEMENT.APPLY_CHANGES,
  id,
  ...changes,
  mergeChanges,
});

const createElement = (position, payload) => ({
  type: EDITOR.ELEMENT.CREATE,
  position,
  payload,
});

const detachParent = id => ({
  type: EDITOR.ELEMENT.DETACH_PARENT,
  id,
});

const assignParent = (id, parentId) => ({
  type: EDITOR.ELEMENT.ASSIGN_PARENT,
  id,
  parentId,
});

const replace = (id, element) => ({
  type: EDITOR.ELEMENT.REPLACE,
  element,
  id,
});

const toggleAddToImageLibrary = image => ({
  type: EDITOR.TOGGLE.ADD_TO_IMAGE_LIBRARY,
  image,
});

const elementsActions = {};

/* requestDelete and delete element */

elementsActions.requestDelete = id => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const { editor: editorState } = getState();
    const { delete_path } = getElement(editorState, id);
    dispatch(autosaveInProgress());
    NProgress.start();
    request
      .delete(delete_path)
      .done(data => {
        // @TODO move this to elementsActions.delete
        if (hasChildren(editorState, id)) {
          dispatch(deleteChildren(id));
        }

        const { sectionsWithInvalidElements } = getState().editor;
        const sectionId = getState().editor.currentSection.id;

        if (!data.sectionHasInvalidElements) {
          dispatch(
            setSectionsWithInvalidElements(
              sectionsWithInvalidElements.filter(
                invalidSectionId => invalidSectionId !== sectionId,
              ),
            ),
          );
        }

        dispatch(autosaveSuccess());
        resolve(deleteElement(id));
        NProgress.done();
      })
      .fail(error => {
        handleError(reject, error);
        dispatch(autosaveFail());
      });
  });

elementsActions.delete = id => (dispatch, getState) => {
  const {
    currentSection: { elements },
    commentary,
  } = getState().editor;
  const isCommenting = commentary.visible && commentary.elementId === id;
  const hasManyElements = elements.length > 1;

  if (isCommenting && hasManyElements) {
    const index = findIndex(elements, { id });
    // @TODO make this a reusable method (e.g. getSibling)
    const sibling = elements[index + 1] || elements[index - 1];
    return dispatch(elementsActions.requestDelete(id))
      .then(action => {
        dispatch(action);
        dispatch(fetchCommentary(sibling.id));
      })
      .catch(dispatch);
  }
  if (isCommenting && !hasManyElements) {
    // @TODO DRY up this (relatively identical to the block above)! 🙅
    return dispatch(elementsActions.requestDelete(id)).then(action => {
      dispatch(action);
      dispatch(hideCommentary());
    });
  }

  return dispatch(elementsActions.requestDelete(id))
    .then(dispatch) // will dispatch elementDelete(id)
    .catch(dispatch); // will dispatch autosaveFail()
};

/* requestUpdate and update element */

function requestUpdate(dispatch, getState, params) {
  return new Promise((resolve, reject) => {
    const { update_path } = getElement(getState().editor, params.id);
    dispatch(autosaveInProgress());
    const put = params.json ? request.putJSON : request.put;
    put(update_path, params.changes)
      .done(data => {
        const hasValidation = has(data, 'metadata.valid');
        if (hasValidation)
          dispatch(setElementValidity(params.id, data.metadata.valid));

        const { sectionsWithInvalidElements } = getState().editor;
        const sectionId = getState().editor.currentSection.id;
        const indexOfSection = sectionsWithInvalidElements.indexOf(sectionId);
        if (data.sectionHasInvalidElements && indexOfSection === -1) {
          // add the current section to the array, if it is not currently present
          // and dispatch the action.
          dispatch(
            setSectionsWithInvalidElements([
              ...sectionsWithInvalidElements,
              sectionId,
            ]),
          );
        } else if (!data.sectionHasInvalidElements) {
          // remove the current section from the array, and dispatch.
          dispatch(
            setSectionsWithInvalidElements(
              sectionsWithInvalidElements.filter(
                invalidSectionId => invalidSectionId !== sectionId,
              ),
            ),
          );
        }

        dispatch(autosaveSuccess());
        resolve(data.metadata);
      })
      .fail(error => handleError(reject, error));
  });
}

elementsActions.requestUpdate =
  (id, changes, { json }) =>
  (dispatch, getState) =>
    requestUpdate(dispatch, getState, { id, changes, json });

elementsActions.update =
  (id, changes, options = { json: false }) =>
  (dispatch, getState) =>
    dispatch(elementsActions.requestUpdate(id, changes, options)).catch(
      dispatch,
    );

elementsActions.updateCriteria = (id, changes) => (dispatch, getState) => {
  const criteriaIds = changes ? changes.map(criterion => criterion.id) : [];
  const criteria = criteriaIds.length === 0 ? '_destroy' : criteriaIds;
  dispatch(elementsActions.update(id, { criteria }));
  dispatch(applyChanges(id, { criteria: changes }, false));
};

elementsActions.updateComments = (id, changes) => (dispatch, getState) => {
  const { comment, commentType, active } = changes;
  const comments = getState().editor.currentSection.elements.find(
    c => c.id === id,
  ).content.question_associated_comments;

  const commentIndex = comments.findIndex(c => c.commentType === commentType);

  const updatedProjects = [
    ...comments.slice(0, commentIndex),
    { ...comments[commentIndex], comment, commentType, active },
    ...comments.slice(commentIndex + 1),
  ];

  dispatch(
    elementsActions.applyChanges(id, {
      question_associated_comments: updatedProjects,
    }),
  );
};

/* requestCopy and copy element */

elementsActions.copy = (params, savePath) => dispatch =>
  new Promise((resolve, reject) => {
    NProgress.start();
    request
      .post(savePath, params)
      .done(data => {
        NProgress.done();
        resolve(data);
      })
      .fail(error => {
        NProgress.done();
        reject(error);
      });
  });

elementsActions.applyChanges =
  (id, changes, options = {}) =>
  (dispatch, getState) =>
    dispatch(applyChanges(id, changes, options.mergeChanges));

elementsActions.replaceElement = replace;

elementsActions.getConvertedDocumentUrl = (id, documentId) => dispatch => {
  dispatch(applyChanges(id, { status: 'converting' }));
  const intervalId = setInterval(() => {
    request
      .get(`/documents/${documentId}/conversion_status`)
      .then(({ url }) => {
        if (url) {
          dispatch(applyChanges(id, { url, status: null }));
          window.clearInterval(intervalId);
        }
      })
      .fail(({ responseJSON: { error } }) => {
        dispatch(applyChanges(id, { status: 'error', errorMessage: error }));
        window.clearInterval(intervalId);
      });
  }, 5000);
};

elementsActions.uploadDocument = (id, documentId) => (dispatch, getState) =>
  uploadAndRecordDocument(documentId)
    .then(url => {
      if (url) {
        dispatch(applyChanges(id, { url }));
      } else {
        dispatch(elementsActions.getConvertedDocumentUrl(id, documentId));
      }
    })
    .catch(({ responseJSON: { error } }) => {
      dispatch(applyChanges(id, { status: 'error', errorMessage: error }));
    });

/**
 * Update image of an image element
 * @param {Object|File} image An image file or a serialized image record object
 * @param {Number} id Id of an image element
 * @param {String} imageType Image type of an element.
 */
elementsActions.handleImageChange =
  (id, image, imageType) => (dispatch, getState) =>
    new Promise((resolve, reject) => {
      dispatch(applyChanges(id, { uploadInProgress: true }));
      const elementType = getElement(getState().editor, id).type;
      const updateImageType =
        imageType === 'content' ? 'content_image' : 'alt_content_image';

      const imageResponse = response => {
        switch (elementType) {
          case 'lesson_content_image':
            return { image: response.image };
          default:
            return {
              content_image: response.content_image,
              alt_content_image: response.alt_content_image,
            };
        }
      };

      if (image instanceof File) {
        // uploading an image ot file stack and setting it to the element
        const { update_path } = getElement(getState().editor, id);
        FileStack.uploadImage(image)
          .then(upload => {
            const params = { filestack_image: upload, image_type: imageType };
            return request.put(update_path, params).then(response => {
              dispatch(
                applyChanges(id, {
                  ...imageResponse(response.metadata),
                  uploadInProgress: false,
                }),
              );
              resolve(response.metadata);
            });
          })
          .catch(reject);
      } else if (image === null) {
        // update image type to null to reset the image for the element
        dispatch(
          elementsActions.update(
            id,
            { [updateImageType]: null },
            { json: true },
          ),
        )
          .then(response => {
            dispatch(
              applyChanges(id, {
                ...imageResponse(response),
                uploadInProgress: false,
              }),
            );
            resolve();
          })
          .catch(reject);
      } else if (typeof image === 'object') {
        // setting an existing image record to the element
        dispatch(
          elementsActions.update(id, {
            image_id: image.id,
            image_type: imageType,
          }),
        )
          .then(response => {
            dispatch(
              applyChanges(id, {
                ...imageResponse(response),
                uploadInProgress: false,
              }),
            );
            resolve();
          })
          .catch(reject);
      } else {
        dispatch(applyChanges(id, { uploadInProgress: false }));
        reject(new Error('Unknow image type'));
      }
    });

elementsActions.create =
  (content_type, position, parent_id) => (dispatch, getState) => {
    const { create_element_path } = getState().editor.currentSection;
    NProgress.start();
    dispatch(autosaveInProgress());
    request
      .post(create_element_path, { content_type, position, parent_id })
      .then(payload => {
        NProgress.done();
        const { sectionsWithInvalidElements } = getState().editor;
        const sectionId = getState().editor.currentSection.id;
        if (
          !payload.publishable &&
          sectionsWithInvalidElements.indexOf(sectionId) === -1
        ) {
          dispatch(
            setSectionsWithInvalidElements([
              ...sectionsWithInvalidElements,
              sectionId,
            ]),
          );
        }

        dispatch(createElement(position, payload));
        dispatch(autosaveSuccess());
      })
      .fail(() => {
        NProgress.done();
        dispatch(autosaveFail());
        alert('There was a problem creating the element. Please try again.'); // eslint-disable-line
      });
  };

elementsActions.orderElement = (id, position, newPosition) => dispatch => {
  dispatch(orderElement(position, newPosition));
  dispatch(elementsActions.update(id, { position: newPosition }));
};

elementsActions.detachParent = (id, newPosition) => (dispatch, getState) => {
  // destructure state
  const state = getState().editor;
  const { position, parent_id: parentId } = getElement(state, id);
  const parent = getElement(state, parentId);
  const { required } = parent.content.options;

  // create updateAction to send changes to the server
  const updateAction = elementsActions.update(id, {
    position: newPosition,
    parent_id: null,
    required,
  });

  dispatch(applyChanges(id, { options: { required } }));
  dispatch(detachParent(id));
  dispatch(orderElement(position, newPosition));
  dispatch(updateAction);
};

elementsActions.assignParent = (childId, parentId) => (dispatch, getState) => {
  // destructure state
  const state = getState().editor;
  const { position: childPosition } = getElement(state, childId);
  const { position: parentPosition, ...parent } = getElement(state, parentId);

  const newPosition =
    childPosition < parentPosition
      ? parentPosition + parent.child_count
      : parentPosition + parent.child_count + 1;
  const { required } = parent.content.options;

  // create updateAction to send changes to the server
  const updateAction = elementsActions.update(childId, {
    parent_id: parentId,
    position: newPosition,
    required,
  });

  dispatch(applyChanges(childId, { options: { required } }));
  dispatch(assignParent(childId, parentId));
  dispatch(orderElement(childPosition, newPosition));
  dispatch(updateAction);
};

elementsActions.updateCommentsCount = () => (dispatch, getState) => {
  const { elementId: id, ...commentary } = getState().editor.commentary;
  dispatch({
    type: EDITOR.ELEMENT.UPDATE_COMMENTS_COUNT,
    id,
    count: getCommentsCount(commentary),
  });
};

elementsActions.toggleDragging = () => dispatch => {
  dispatch({
    type: EDITOR.TOGGLE.DRAGGING_ELEMENT,
  });
};

elementsActions.toggleAddToImageLibrary = toggleAddToImageLibrary;

elementsActions.addSavedContentToLesson =
  (lessonId, sectionId, savedContentId, position, isTemplate, templateId) =>
  dispatch => {
    const path = isTemplate
      ? routes.TEMPLATE_COPY_SAVED_ELEMENT_CONTENT(
          templateId,
          sectionId,
          savedContentId,
        )
      : routes.COPY_SAVED_ELEMENT_CONTENT(lessonId, sectionId, savedContentId);

    return new Promise((resolve, reject) => {
      NProgress.start();
      request
        .post(path, { position })
        .done(data => {
          NProgress.done();
          data.forEach(element => {
            dispatch(createElement(position, element));

            if (
              data.some(
                _ => _.element_section_has_unpublishable_elements === true,
              )
            ) {
              // add the current section to the array, if it is not currently present
              // and dispatch the action.
              dispatch(setSectionsWithInvalidElements([...[], sectionId]));
            }
          });
          resolve(data);
        })
        .fail(error => {
          NProgress.done();
          reject(error);
        });
    });
  };

elementsActions.createZendeskTicket =
  (content_type, position, zendesk_ticket_id) => (dispatch, getState) => {
    const { create_element_path } = getState().editor.currentSection;

    return new Promise((resolve, reject) => {
      NProgress.start();
      request
        .post(create_element_path, {
          content_type,
          position,
          zendesk_ticket_id,
        })
        .then(payload => {
          NProgress.done();
          dispatch(createElement(position, payload));
          resolve(payload);
        })
        .fail(() => {
          alert('There was a problem creating the element. Please try again.'); // eslint-disable-line
        });
    });
  };

elementsActions.unlinkElement = elementId => (dispatch, getState) => {
  const { elements } = getState().editor.currentSection;
  const element = find(elements, { id: elementId });
  NProgress.start();
  request
    .post(element.unlink_path)
    .then(unlinkedElement => {
      NProgress.done();
      dispatch(elementsActions.replaceElement(elementId, unlinkedElement));
    })
    .fail(() => {
      NProgress.done();
      alert(t('common.server_error')); // eslint-disable-line
    });
};

elementsActions.requestUpdateAndApplyChanges =
  (id, changes) => (dispatch, getState) =>
    requestUpdate(dispatch, getState, {
      id,
      changes,
      json: false,
    })
      .then(response => {
        dispatch(
          elementsActions.applyChanges(
            id,
            { ...response },
            { mergeChanges: false },
          ),
        );
      })
      .catch(() => dispatch(autosaveFail()));

export default elementsActions;
